import { useEffect, useState } from 'react';
import { AppShell } from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { payslipsApi, type PayslipDetail, type PayslipHistoryItem } from '../lib/api/payslips';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';

const FAQ = [
  {
    q: 'Sao thuế TNCN tháng này cao hơn?',
    a: 'Do trong kỳ này có thưởng bổ sung. Thuế TNCN áp dụng biểu lũy tiến 7 bậc, khi tổng thu nhập vượt ngưỡng bậc trên sẽ chịu suất cao hơn.',
  },
  {
    q: 'Cách đăng ký người phụ thuộc?',
    a: 'Nộp Form 07/XN-NPT có xác nhận UBND phường/xã + CMND/CCCD người phụ thuộc + giấy khai sinh, nộp cho Phòng Kế toán. Mỗi NPT giảm trừ 4.4tr/tháng.',
  },
  {
    q: 'Khi nào tiền lương vào TK?',
    a: 'Bảng lương được KTT + CEO duyệt trước ngày 5 hàng tháng, tiền vào TK trong 2 ngày làm việc.',
  },
];

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Chờ lập', PENDING_KTT: 'Chờ KTT', PENDING_CEO: 'Chờ CEO ký',
  APPROVED: 'Chờ chi', DISBURSED: 'Đã chi', REJECTED: 'Bị từ chối',
};

export function MyPayslipsPage() {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const [current, setCurrent] = useState<PayslipDetail | null>(null);
  const [history, setHistory] = useState<PayslipHistoryItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<PayslipDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  useEffect(() => {
    setLoading(true); setErr(null);
    Promise.all([payslipsApi.current(), payslipsApi.list()])
      .then(([c, h]) => { setCurrent(c); setHistory(h.items); })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    payslipsApi.detail(selectedId).then(setSelected).catch(() => {});
  }, [selectedId]);

  const displayed = selected ?? current;
  const firstName = (user?.name || '').split(' ').pop() || '';

  return (
    <AppShell title={`Phiếu lương ${displayed?.period ?? ''}`} subtitle={`Chào ${firstName || user?.name} 👋`}>
      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      {loading && <div className="pay-empty">Đang tải phiếu lương…</div>}

      {!loading && !displayed && (
        <div className="dsh-card" style={{ textAlign: 'center', padding: 40 }}>
          <h3>Chưa có phiếu lương</h3>
          <p>Bạn chưa có phiếu lương nào trong hệ thống. Liên hệ Phòng Kế toán qua <a href="mailto:ketoan@hitek.vn">ketoan@hitek.vn</a>.</p>
        </div>
      )}

      {displayed && (
        <>
          {/* Hero */}
          <div className="mps-hero">
            <div className="mps-hero-top">
              <span className="mps-hero-badge">{STATUS_LABEL[displayed.status] ?? displayed.status}</span>
              <span className="mps-hero-period">
                Kỳ {displayed.period}
                {displayed.disbursedAt && ` · Chi ngày ${new Date(displayed.disbursedAt).toLocaleDateString('vi-VN')}`}
              </span>
            </div>
            <div className="mps-hero-net">
              <div className="mps-hero-label">Thực lĩnh</div>
              <div className="mps-hero-amount">{formatVND(displayed.net)}</div>
              <div className="mps-hero-delta">
                {displayed.workingDays != null && `${displayed.workingDays} ngày công`}
                {displayed.otHours != null && displayed.otHours > 0 && ` · ${displayed.otHours}h OT`}
              </div>
            </div>
            <div className="mps-hero-actions">
              <button className="pay-btn-outline" type="button" onClick={() => window.print()}>Xem chi tiết + In</button>
              <button className="pay-btn-outline" type="button" onClick={() => { window.print(); showToast('Chọn “Lưu dưới dạng PDF” trong hộp in để tải phiếu lương.'); }}>Tải PDF</button>
              <button className="pay-btn-primary" type="button" onClick={() => { setOpenFaq(0); document.getElementById('payslip-faq')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Hỏi AI về phiếu này</button>
            </div>
          </div>

          {/* Breakdown */}
          <div className="mps-breakdown">
            <div className="mps-col mps-col-income">
              <div className="mps-col-head"><h3>Tổng thu nhập</h3><b>{formatVND(displayed.income.total)}</b></div>
              <ul>
                <li><span>Lương cơ bản (đóng BH)</span><b>{formatVND(displayed.income.baseSalary)}</b></li>
                <li><span>Lương thoả thuận</span><b>{formatVND(displayed.income.agreedSalary)}</b></li>
                {displayed.income.overtimeAmount > 0 && <li><span>Tăng ca 150%</span><b>{formatVND(displayed.income.overtimeAmount)}</b></li>}
                {displayed.income.allowanceTaxFree > 0 && <li><span>PC miễn thuế</span><b>{formatVND(displayed.income.allowanceTaxFree)}</b></li>}
                {displayed.income.allowanceTaxable > 0 && <li><span>PC chịu thuế</span><b>{formatVND(displayed.income.allowanceTaxable)}</b></li>}
                {displayed.income.bonusAmount > 0 && <li><span>Thưởng</span><b>{formatVND(displayed.income.bonusAmount)}</b></li>}
                <li className="mps-col-total"><span>Cộng</span><b>{formatVND(displayed.income.total)}</b></li>
              </ul>
            </div>
            <div className="mps-col mps-col-deduct">
              <div className="mps-col-head"><h3>Tổng khấu trừ</h3><b>{formatVND(displayed.deduct.total)}</b></div>
              <ul>
                <li><span>BHXH 8%</span><b>{formatVND(displayed.deduct.bhxhNv)}</b></li>
                <li><span>BHYT 1.5%</span><b>{formatVND(displayed.deduct.bhytNv)}</b></li>
                <li><span>BHTN 1%</span><b>{formatVND(displayed.deduct.bhtnNv)}</b></li>
                <li><span>Thuế TNCN</span><b>{formatVND(displayed.deduct.personalIncomeTax)}</b></li>
                {displayed.deduct.advanceDeduct > 0 && <li><span>Tạm ứng trừ</span><b>{formatVND(displayed.deduct.advanceDeduct)}</b></li>}
                <li className="mps-col-total"><span>Cộng</span><b>{formatVND(displayed.deduct.total)}</b></li>
              </ul>
            </div>
          </div>

          <div className="mps-pdf-note">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" /></svg>
            Mật khẩu PDF: <b>4 số cuối CCCD của bạn</b>. Đừng chia sẻ file với người khác.
          </div>
        </>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="dsh-card">
          <div className="dsh-card-head">
            <h3>Lịch sử phiếu lương ({history.length})</h3>
          </div>
          <ul className="mps-history">
            {history.map((h) => (
              <li key={h.id}>
                <span className="mps-h-month">{h.period}</span>
                <span className={`mps-h-status ${h.status === 'DISBURSED' ? 'paid' : 'pending'}`}>
                  {h.status === 'DISBURSED' ? 'Đã chi' : STATUS_LABEL[h.status] ?? h.status}
                </span>
                <b>{formatVND(h.net)}</b>
                <button
                  type="button"
                  className="mps-h-btn"
                  onClick={() => setSelectedId(h.payrollId)}
                >
                  {selectedId === h.payrollId ? 'Đang xem' : 'Xem →'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* FAQ */}
      <div className="dsh-card">
        <div className="dsh-card-head"><h3>Câu hỏi thường gặp</h3></div>
      <div id="payslip-faq" className="mps-faq">
          {FAQ.map((f, i) => (
            <div key={i} className={`mps-faq-item ${openFaq === i ? 'open' : ''}`}>
              <button
                type="button"
                className="mps-faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{f.q}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
              </button>
              {openFaq === i && <div className="mps-faq-a">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="mps-contact">
        Cần trợ giúp? Liên hệ Phòng Kế toán qua <a href="mailto:ketoan@hitek.vn">ketoan@hitek.vn</a> hoặc <b>028.1234.5678</b>.
      </div>

      {toast && <div className="coa-toast">{toast}</div>}
    </AppShell>
  );
}
